import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../../App';
import AppLayout from '../../components/layouts/AppLayout';
import ResponsiveNavbar from '../../components/ResponsiveNavbar';
import { CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminInvestors() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');

  const loadApplications = useCallback(async () => {
    setLoading(true); // Reset loading when filter changes
    try {
      console.log(`🔍 Loading applications with status: ${filter}`);
      const response = await api.get(`/applications?status=${filter}`);
      console.log(`✅ Received ${response.data.length} applications:`, response.data);
      setApplications(response.data);
    } catch (error) {
      console.error('❌ Failed to load applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const handleApprove = async (applicationId) => {
    try {
      await api.post(`/applications/${applicationId}/approve`);
      toast.success('Application approved');
      loadApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Approval failed');
    }
  };

  const handleReject = async (applicationId) => {
    const reason = prompt('Rejection reason (optional):');
    try {
      await api.post(`/applications/${applicationId}/reject`, null, { params: { reason } });
      toast.success('Application rejected');
      loadApplications();
    } catch (error) {
      toast.error('Rejection failed');
    }
  };

  return (
    <AppLayout>
      <div className="min-h-screen" data-testid="admin-investors-page">
        {/* Responsive Navigation */}
        <ResponsiveNavbar isAdmin={true} />

        {/* Content */}
        <div className="container-responsive max-w-7xl mx-auto py-6 sm:py-8">
        <h1 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl font-bold mb-6">Investor Applications</h1>

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-6">
          {['pending', 'approved', 'rejected'].map(status => (
            <button key={status} onClick={() => setFilter(status)} className={`px-4 py-2.5 sm:py-2 text-sm sm:text-base rounded-lg font-medium transition-colors ${filter === status ? 'bg-[#D4AF37] text-[#0A1128]' : 'bg-white/5 text-[#94A3B8] hover:bg-white/10'}`}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto"></div></div>
        ) : applications.length === 0 ? (
          <div className="premium-card p-12 text-center"><p className="text-[#64748B]">No {filter} applications</p></div>
        ) : (
          <div className="space-y-4">
            {applications.map((app, index) => (
              <div key={`${app.application_id || 'unknown'}-${index}`} className="premium-card p-6" data-testid={`application-${app.application_id}`}>
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                  <div className="flex-1">
                    <h3 className="font-['Outfit'] text-xl font-bold mb-2">{app.name}</h3>
                    <p className="text-[#94A3B8] mb-1">{app.email}</p>
                    {app.phone && <p className="text-[#64748B] text-sm">{app.phone}</p>}
                    <p className="text-[#64748B] text-xs mt-2">Submitted: {new Date(app.created_at).toLocaleDateString()}</p>
                    {app.rejection_reason && <p className="text-[#EF4444] text-sm mt-2">Reason: {app.rejection_reason}</p>}
                  </div>
                  {app.status === 'pending' && (
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto mt-4 sm:mt-0">
                      <button onClick={() => handleApprove(app.application_id)} className="px-3 py-2 sm:px-4 text-sm sm:text-base bg-[#10B981] text-white rounded-lg hover:bg-[#059669] transition-colors flex items-center justify-center gap-2" data-testid={`approve-btn-${app.application_id}`}>
                        <CheckCircle className="w-4 h-4" />
                        <span>Approve</span>
                      </button>
                      <button onClick={() => handleReject(app.application_id)} className="px-3 py-2 sm:px-4 text-sm sm:text-base bg-[#EF4444] text-white rounded-lg hover:bg-[#DC2626] transition-colors flex items-center justify-center gap-2" data-testid={`reject-btn-${app.application_id}`}>
                        <XCircle className="w-4 h-4" />
                        <span>Reject</span>
                      </button>
                    </div>
                  )}
                  {app.status === 'approved' && (
                    <div className="flex items-center gap-2 text-[#10B981] mt-4 sm:mt-0">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-semibold">Approved</span>
                    </div>
                  )}
                  {app.status === 'rejected' && (
                    <div className="flex items-center gap-2 text-[#EF4444] mt-4 sm:mt-0">
                      <XCircle className="w-5 h-5" />
                      <span className="font-semibold">Rejected</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </AppLayout>
  );
}
